import { createAsyncThunk } from '@reduxjs/toolkit';

import axiosInstance, { isAxiosError, isCancel } from '@/utils/axios';
import { hospitalHeaders } from '@/utils';
import { getErrorMessage } from '@/utils/error-message';
import { getTranscriptFileContentType } from '@/utils/meeting-transcription/extract-transcript';
import type {
    ClearMeetingTranscriptOverridesParams,
    ClearMeetingTranscriptOverridesResponse,
    FetchMeetingTranscriptHistoryParams,
    FetchMeetingTranscriptPdfParams,
    MeetingTranscriptActiveResponse,
    MeetingTranscriptHistoryResponse,
    MeetingTranscriptInfo,
    MeetingTranscriptJobParams,
    MeetingTranscriptMatchesResponse,
    MeetingTranscriptUploadUrlResponse,
    SaveMeetingTranscriptMatchesParams,
    UploadMeetingTranscriptPayload
} from '@/types/meeting-transcripts';

const HISTORY_DEFAULT_PAGE = 1;
const HISTORY_DEFAULT_LIMIT = 20;

const jobPath = (id: string) => `meeting-transcripts/${encodeURIComponent(id)}`;

/** `GET /meeting-transcripts/hospitals/:hospitalId/active` — resume in-progress or failed pipeline. */
export const fetchActiveMeetingTranscript = createAsyncThunk<
    MeetingTranscriptActiveResponse,
    { hospitalId: string },
    { rejectValue: string }
>('meetingTranscripts/fetchActive', async ({ hospitalId }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<MeetingTranscriptActiveResponse>(
            `meeting-transcripts/hospitals/${encodeURIComponent(hospitalId)}/active`,
            {
                ...hospitalHeaders(hospitalId),
                signal
            }
        );
        return { active: data.active ?? null };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        // Stable markers so callers can ignore transient blips without clearing the job.
        if (isAxiosError(error)) {
            if (error.response?.status === 502) {
                return rejectWithValue('502 Bad Gateway');
            }
            // Browser reports dropped/CORS/unreachable as ERR_NETWORK with no HTTP status.
            if (error.code === 'ERR_NETWORK' || (!error.response && /network error/i.test(error.message ?? ''))) {
                return rejectWithValue('Network Error');
            }
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch active meeting transcript'));
    }
});

/**
 * Presigned upload:
 * 1. `POST /meeting-transcripts/upload-url` — metadata + signed PUT URL
 * 2. `PUT uploadUrl` — raw file to S3 (same Content-Type as step 1)
 * 3. `POST /meeting-transcripts/:id/complete` — start matching
 */
export const uploadMeetingTranscript = createAsyncThunk<MeetingTranscriptInfo, UploadMeetingTranscriptPayload, { rejectValue: string }>(
    'meetingTranscripts/upload',
    async ({ file, hospitalId, meetingDate, meetingType }, { rejectWithValue, signal }) => {
        try {
            const contentType = getTranscriptFileContentType(file);
            const { data } = await axiosInstance.post<MeetingTranscriptUploadUrlResponse>(
                'meeting-transcripts/upload-url',
                {
                    hospitalId,
                    meetingDate,
                    meetingType: meetingType.trim(),
                    fileName: file.name,
                    contentType
                },
                {
                    ...hospitalHeaders(hospitalId),
                    signal
                }
            );

            const putResponse = await fetch(data.uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': contentType },
                body: file,
                signal
            });
            if (!putResponse.ok) {
                return rejectWithValue(`Failed to upload meeting transcript file (${putResponse.status})`);
            }

            const { data: completed } = await axiosInstance.post<MeetingTranscriptInfo>(
                `${jobPath(data.transcript.id)}/complete`,
                {},
                {
                    ...hospitalHeaders(hospitalId),
                    signal
                }
            );
            return completed?.id ? completed : data.transcript;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to upload meeting transcript'));
        }
    }
);

/** `GET /meeting-transcripts/:id` — optional `X-Hospital-Id`. */
export const fetchMeetingTranscript = createAsyncThunk<MeetingTranscriptInfo, MeetingTranscriptJobParams, { rejectValue: string }>(
    'meetingTranscripts/fetchOne',
    async ({ id, hospitalId }, { rejectWithValue, signal }) => {
        try {
            const { data } = await axiosInstance.get<MeetingTranscriptInfo>(jobPath(id), {
                ...hospitalHeaders(hospitalId),
                signal
            });
            return data;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to fetch meeting transcript'));
        }
    }
);

/** `GET /meeting-transcripts` — query: `page`, `limit`, `hospitalId`. */
export const fetchMeetingTranscriptHistory = createAsyncThunk<
    MeetingTranscriptHistoryResponse,
    FetchMeetingTranscriptHistoryParams,
    { rejectValue: string }
>('meetingTranscripts/fetchHistory', async ({ hospitalId, page, limit }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<MeetingTranscriptHistoryResponse>('meeting-transcripts', {
            params: {
                page: page ?? HISTORY_DEFAULT_PAGE,
                limit: limit ?? HISTORY_DEFAULT_LIMIT,
                hospitalId
            },
            ...hospitalHeaders(hospitalId),
            signal
        });
        return {
            items: data.items ?? [],
            total: data.total ?? 0,
            page: data.page ?? page ?? HISTORY_DEFAULT_PAGE,
            limit: data.limit ?? limit ?? HISTORY_DEFAULT_LIMIT
        };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch meeting transcript history'));
    }
});

/** `GET /meeting-transcripts/:id/matches` — 400 while status is `uploaded` or `matching`. */
export const fetchMeetingTranscriptMatches = createAsyncThunk<
    MeetingTranscriptMatchesResponse,
    MeetingTranscriptJobParams,
    { rejectValue: string }
>('meetingTranscripts/fetchMatches', async ({ id, hospitalId }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.get<MeetingTranscriptMatchesResponse>(`${jobPath(id)}/matches`, {
            ...hospitalHeaders(hospitalId),
            signal
        });
        return { items: data.items ?? [] };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to fetch meeting transcript matches'));
    }
});

/** `PATCH /meeting-transcripts/:id/matches` — batch verification; auto-generate when all rows are resolved. */
export const saveMeetingTranscriptMatches = createAsyncThunk<
    MeetingTranscriptMatchesResponse,
    SaveMeetingTranscriptMatchesParams,
    { rejectValue: string }
>('meetingTranscripts/saveMatches', async ({ id, hospitalId, matches }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.patch<MeetingTranscriptMatchesResponse>(
            `${jobPath(id)}/matches`,
            { matches },
            {
                ...hospitalHeaders(hospitalId),
                signal
            }
        );
        return { items: data.items ?? [] };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to save meeting transcript matches'));
    }
});

/** `POST /meeting-transcripts/:id/generate` — optional manual generate before all rows are resolved. */
export const generateMeetingTranscript = createAsyncThunk<MeetingTranscriptInfo, MeetingTranscriptJobParams, { rejectValue: string }>(
    'meetingTranscripts/generate',
    async ({ id, hospitalId }, { rejectWithValue, signal }) => {
        try {
            const { data } = await axiosInstance.post<MeetingTranscriptInfo>(
                `${jobPath(id)}/generate`,
                {},
                {
                    ...hospitalHeaders(hospitalId),
                    signal
                }
            );
            return data;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to start meeting transcript generation'));
        }
    }
);

/** `POST /meeting-transcripts/:id/retry` — retries `failedStage` only (`match` | `generate` | `pdf_email`). */
export const retryMeetingTranscript = createAsyncThunk<MeetingTranscriptInfo, MeetingTranscriptJobParams, { rejectValue: string }>(
    'meetingTranscripts/retry',
    async ({ id, hospitalId }, { rejectWithValue, signal }) => {
        try {
            const { data } = await axiosInstance.post<MeetingTranscriptInfo>(
                `${jobPath(id)}/retry`,
                {},
                {
                    ...hospitalHeaders(hospitalId),
                    signal
                }
            );
            return data;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to retry meeting transcript'));
        }
    }
);

/** `DELETE /meeting-transcripts/:id` — cancel in-flight job; unblocks a new upload. */
export const cancelMeetingTranscript = createAsyncThunk<MeetingTranscriptInfo, MeetingTranscriptJobParams, { rejectValue: string }>(
    'meetingTranscripts/cancel',
    async ({ id, hospitalId }, { rejectWithValue, signal }) => {
        try {
            const { data } = await axiosInstance.delete<MeetingTranscriptInfo>(jobPath(id), {
                ...hospitalHeaders(hospitalId),
                signal
            });
            return data;
        } catch (error: unknown) {
            if (isCancel(error)) {
                throw error;
            }
            return rejectWithValue(getErrorMessage(error, 'Failed to cancel meeting transcript'));
        }
    }
);

/** `GET /meeting-transcripts/hospitals/:hospitalId/:id/pdf` — PDF binary (not a Redux thunk — Blobs are non-serializable). */
export const fetchMeetingTranscriptPdf = async ({
    hospitalId,
    id,
    signal
}: FetchMeetingTranscriptPdfParams & { signal?: AbortSignal }): Promise<Blob> => {
    try {
        const { data } = await axiosInstance.get<Blob>(
            `meeting-transcripts/hospitals/${encodeURIComponent(hospitalId)}/${encodeURIComponent(id)}/pdf`,
            {
                ...hospitalHeaders(hospitalId),
                responseType: 'blob',
                signal
            }
        );
        return data;
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        throw new Error(getErrorMessage(error, 'Failed to download meeting transcript PDF'));
    }
};

/** `POST /meeting-transcripts/overrides/clear` — body: `{ fins }`; requires `X-Hospital-Id`. */
export const clearMeetingTranscriptOverrides = createAsyncThunk<
    ClearMeetingTranscriptOverridesResponse,
    ClearMeetingTranscriptOverridesParams,
    { rejectValue: string }
>('meetingTranscripts/clearOverrides', async ({ hospitalId, fins }, { rejectWithValue, signal }) => {
    try {
        const { data } = await axiosInstance.post<ClearMeetingTranscriptOverridesResponse>(
            'meeting-transcripts/overrides/clear',
            { fins },
            {
                ...hospitalHeaders(hospitalId),
                signal
            }
        );
        return { clearedCount: data.clearedCount ?? 0 };
    } catch (error: unknown) {
        if (isCancel(error)) {
            throw error;
        }
        return rejectWithValue(getErrorMessage(error, 'Failed to clear meeting transcript overrides'));
    }
});
